import { useEffect, useRef, useCallback } from 'react';
import { User, OrderType } from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	UISerializableOrder,
	UIMarket,
	getOrderLabelFromOrderDetails,
} from '@velocity-exchange/common';
import { formatText, PRESETS } from '@velocity-exchange/common/format';
import { notify } from 'src/utils/notifications';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import { getChainClockTs } from 'src/hooks/useChainClockTs';
import { getAllNonZeroOrders } from 'src/hooks/query/accountTradingData/utils/computeOrders';
import { getExpiredOrderIds } from 'src/utils/orderExpiry';
import { isOrderTrackedByMarketOrderToast } from 'src/components/MarketOrderToasts/marketOrderToastTracking';

// Expiry is time-driven, so a plain account-update listener isn't enough — we also
// poll on a short interval to catch orders that cross `maxTs` without any account change.
const CHECK_INTERVAL_MS = 2000;

/** True for order types that rest at a meaningful price worth showing in the toast. */
function orderHasRelevantPrice(order: UISerializableOrder): boolean {
	const isMarket =
		ENUM_UTILS.match(order.orderType, OrderType.MARKET) ||
		ENUM_UTILS.match(order.orderType, OrderType.ORACLE);
	return !isMarket && !!order.price?.gtZero();
}

/**
 * Detects orders that expire (pass `maxTs` + hide buffer) while the app is open and
 * fires a one-off "order expired" notification. One instance runs per subscribed
 * subaccount, so expiry is surfaced even for subaccounts that aren't currently active.
 *
 * - Only live transitions toast: orders already expired at first evaluation (e.g. on
 *   reload) are seeded as "seen" and silently hidden, never toasted.
 * - Orders already tracked by the MarketOrderToasts system are skipped — their existing
 *   progress toast already surfaces expiry (see `marketOrderToastTracking`).
 * - When the expiring order belongs to a non-active subaccount, its name is appended to
 *   the toast so the user can tell which subaccount it came from.
 */
export function useExpiredOrderNotifications(
	userKey: string | undefined,
	user: User | undefined
) {
	// orderIds we've already accounted for (seeded on first load, then accumulated as
	// orders expire).
	const seenExpiredIdsRef = useRef<Set<number>>(new Set());
	const isFirstLoadRef = useRef(true);

	// Reset when switching accounts to avoid stale/false notifications.
	useEffect(() => {
		seenExpiredIdsRef.current = new Set();
		isFirstLoadRef.current = true;
	}, [userKey]);

	const checkForExpiredOrders = useCallback(
		(currentUser: User) => {
			const chainTs = getChainClockTs();

			// Defer the first-load seed until the chain clock has been observed. Until
			// then `getExpiredOrderIds` fail-safes to an empty set, so seeding now would
			// leave orders already expired on load unseeded and spuriously toast them
			// once the clock arrives.
			if (isFirstLoadRef.current && !chainTs) return;

			const nonZeroOrders = getAllNonZeroOrders(currentUser);
			const expiredIds = getExpiredOrderIds(nonZeroOrders, chainTs);

			// First evaluation: seed what's already expired without notifying.
			if (isFirstLoadRef.current) {
				isFirstLoadRef.current = false;
				seenExpiredIdsRef.current = new Set(expiredIds);
				return;
			}

			const ownerKey = currentUser.getUserAccountPublicKey().toString();

			// Collect the orders that just expired (excluding any already surfaced by the
			// MarketOrderToasts progress toast) so simultaneous expiries — e.g. a batch of
			// scaled orders sharing a `maxTs` — coalesce into a single notification.
			const newlyExpired: UISerializableOrder[] = [];
			for (const order of nonZeroOrders) {
				if (!expiredIds.has(order.orderId)) continue;
				if (seenExpiredIdsRef.current.has(order.orderId)) continue;

				seenExpiredIdsRef.current.add(order.orderId);

				// The MarketOrderToasts system already toasts orders it tracks.
				if (isOrderTrackedByMarketOrderToast(ownerKey, order.orderId)) continue;

				newlyExpired.push(order);
			}

			if (newlyExpired.length === 0) return;

			// Label the toast with the subaccount name when it isn't the active one, so
			// cross-subaccount expiries are distinguishable.
			const accountsState = useVelocityAccountStore.getState();
			const isActiveSubaccount = accountsState.currentUserKey === userKey;
			const subaccountName =
				accountsState.accounts[userKey ?? '']?.name || 'another subaccount';
			const subaccountSuffix = isActiveSubaccount
				? ''
				: ` on ${subaccountName}`;

			if (newlyExpired.length > 1) {
				notify({
					type: 'error',
					message: 'Orders Expired',
					description: `${newlyExpired.length} orders${subaccountSuffix} expired without filling.`,
				});
				return;
			}

			const order = newlyExpired[0];
			const uiMarket = new UIMarket(order.marketIndex, order.marketType);
			const marketName = uiMarket.uiSymbols.marketDisplaySymbol;
			const orderType = getOrderLabelFromOrderDetails(order);
			const baseSize = formatText(order.baseAssetAmount, PRESETS.orderSize);
			const priceSuffix = orderHasRelevantPrice(order)
				? ` at $${formatText(order.price?.toNum() ?? 0, PRESETS.priceText)}`
				: '';

			notify({
				type: 'error',
				message: `${marketName} ${orderType} Expired`,
				description: `Your ${baseSize} ${marketName} ${orderType} order${priceSuffix}${subaccountSuffix} expired without filling.`,
			});
		},
		[userKey]
	);

	useEffect(() => {
		if (!userKey || !user?.isSubscribed) return;

		const handler = () => checkForExpiredOrders(user);

		handler();

		user.eventEmitter.on('userAccountUpdate', handler);
		const interval = setInterval(handler, CHECK_INTERVAL_MS);

		return () => {
			user.eventEmitter.removeListener('userAccountUpdate', handler);
			clearInterval(interval);
		};
	}, [userKey, user, checkForExpiredOrders]);
}
